import { useCallback, useEffect, useRef, useState } from "react";
import useVisibleInterval from "../../hooks/useVisibleInterval";
import { getJob, listJobs } from "../../services/bulkReports";
import { bulkReportErrorMessage } from "./bulkReportErrors";
import { ACTIVE_STATUSES, isActiveJob } from "./bulkReportJobs";

export const POLL_MS = 3000;
export const MAX_POLL_MS = 30000;
export const PER_PAGE = 25;
// The poll asks for every in-progress job at once; a user has at most three
// and the API caps a page at 100.
const POLL_PER_PAGE = 100;
// A poll is retried by the next tick, never by apiClient's 5xx retry.
const NO_RETRY = { retryServerErrors: false };
// One followed job's own read: a failure costs only that row, which keeps its
// last state and is read again next tick. The list read alone decides whether
// the poll failed.
const readOne = (id) => getJob(id, NO_RETRY).catch(() => null);

// 3 s, then 6, 12, 24, 30 s after consecutive failed polls; back to 3 s on success.
export const pollDelay = (failures) => Math.min(POLL_MS * 2 ** failures, MAX_POLL_MS);

/**
 * The reports table's data (ASB-4295): one page of the ArcTag's jobs from the
 * last 30 days (the API's default), "mine" (My Reports) or "all".
 *
 * Polling follows the brief's rule, because every GET /jobs runs the API's
 * housekeeping: ONE list call (status=queued,running, same mine filter) every
 * 3 s, only while the rows on screen include a queued or running job and the
 * tab is visible. A polled job that is no longer in progress reloads the page
 * of rows, which brings its final state. Failed polls back off to 30 s.
 *
 * Only the newest request may land (ArcLens' latestRequest pattern): a toggle,
 * a page change, the unmount (an ArcTag switch remounts the tool) and every
 * local change (a submit, a cancel, an expired download) make each earlier
 * response stale. A local change then reloads, so a list read before it can
 * never erase a new row or bring a cancelled job back as running.
 *
 * A deep-linked job off this page or filter is "pinned" above the rows. While
 * it is in progress it is updated from the poll's results; when the poll
 * cannot show it (a colleague's job under My Reports) it is read with getJob.
 *
 * Jobs this page submitted are followed the same way until they finish, even
 * on another page of the table: the poll keeps running for them, and one that
 * leaves the in-progress list off screen is read with getJob so its finish is
 * reported (the auto-download and failure handling depend on it).
 *
 * onJobs(jobs) is called with each list that lands, full or polled, so the
 * page can act on jobs that finished (auto-download, failure handling).
 */
export default function useBulkReportJobs(tagId, { onJobs } = {}) {
	const [view, setViewState] = useState("mine");
	const [page, setPage] = useState(1);
	const [jobs, setJobs] = useState([]);
	const [pagination, setPagination] = useState(null);
	const [loading, setLoading] = useState(true);
	const [loadError, setLoadError] = useState("");
	const [pollError, setPollError] = useState("");
	const [pollFailures, setPollFailures] = useState(0);
	// Set after the first load lands (or fails), for the ?job= deep link.
	const [loaded, setLoaded] = useState(false);
	const [pinned, setPinnedState] = useState(null);
	// This page's own submissions: id -> last known job.
	const [tracked, setTracked] = useState({});
	const trackedRef = useRef(tracked);
	trackedRef.current = tracked;

	const latestRequest = useRef(0);
	const loadInFlight = useRef(false);
	const jobsRef = useRef(jobs);
	jobsRef.current = jobs;
	const pinnedRef = useRef(pinned);
	pinnedRef.current = pinned;
	// Jobs a 410 proved expired. The API writes "expired" on that refusal, but
	// a read already in flight may still say succeeded; the row stays Expired.
	const expiredIds = useRef(new Set());
	const onJobsRef = useRef(onJobs);
	onJobsRef.current = onJobs;

	const withLocal = (job) =>
		job && expiredIds.current.has(job.id) && job.status === "succeeded"
			? { ...job, status: "expired", download_available: false }
			: job;
	const report = (list) => {
		if (onJobsRef.current && list.length) onJobsRef.current(list);
	};
	// Keep the followed submissions' last known state.
	const track = (list) => {
		const known = trackedRef.current;
		const updates = list.filter((job) => job && known[job.id]);
		if (updates.length) setTracked((prev) => ({ ...prev, ...Object.fromEntries(updates.map((j) => [j.id, j])) }));
	};

	const load = useCallback(async () => {
		const request = ++latestRequest.current;
		loadInFlight.current = true;
		setLoading(true);
		try {
			const result = await listJobs(tagId, { mine: view === "mine", page, perPage: PER_PAGE });
			if (request !== latestRequest.current) return;
			const rows = result.jobs.map(withLocal);
			setJobs(rows);
			setPagination(result.pagination || null);
			setLoadError("");
			setPollError("");
			setPollFailures(0);
			track(rows);
			report(rows);
		} catch (err) {
			if (request !== latestRequest.current) return;
			// A failed read must not read as "no reports": clear the rows and say so.
			setJobs([]);
			setPagination(null);
			setLoadError(bulkReportErrorMessage(err));
		} finally {
			if (request === latestRequest.current) {
				loadInFlight.current = false;
				setLoading(false);
				setLoaded(true);
			}
		}
	}, [tagId, view, page]);

	useEffect(() => {
		load();
		return () => {
			// Unmount or a new filter/page: whatever is in flight is stale.
			latestRequest.current += 1;
			loadInFlight.current = false;
		};
	}, [load]);

	const poll = useCallback(async () => {
		// A full load already in flight answers for this tick.
		if (loadInFlight.current) return;
		const request = ++latestRequest.current;
		const watchedPin = isActiveJob(pinnedRef.current) ? pinnedRef.current : null;
		const followed = Object.values(trackedRef.current).filter(isActiveJob);
		let active;
		let pinUpdate = null;
		let followedUpdates = [];
		try {
			({ jobs: active } = await listJobs(
				tagId,
				{ mine: view === "mine", status: ACTIVE_STATUSES, perPage: POLL_PER_PAGE },
				NO_RETRY,
			));
			// The pinned job is not in the in-progress list: it finished, or this
			// filter cannot show it. Read it directly.
			if (watchedPin && !active.some((j) => j.id === watchedPin.id)) {
				pinUpdate = await readOne(watchedPin.id);
			}
			// A submission that left the in-progress list while off screen: read
			// its final state (on screen, the page reload below brings it).
			const offScreen = followed.filter(
				(j) =>
					!active.some((a) => a.id === j.id) &&
					!jobsRef.current.some((r) => r.id === j.id) &&
					j.id !== watchedPin?.id,
			);
			followedUpdates = await Promise.all(offScreen.map((j) => readOne(j.id)));
		} catch (err) {
			if (request !== latestRequest.current) return;
			setPollError(bulkReportErrorMessage(err));
			setPollFailures((n) => n + 1);
			return;
		}
		if (request !== latestRequest.current) return;
		setPollError("");
		setPollFailures(0);
		const byId = new Map(active.map((job) => [job.id, withLocal(job)]));
		if (watchedPin) {
			const next = withLocal(byId.get(watchedPin.id) || pinUpdate);
			if (next) setPinnedState((p) => (p && p.id === next.id ? next : p));
		}
		const finished = jobsRef.current.some((job) => isActiveJob(job) && !byId.has(job.id));
		setJobs((prev) => prev.map((job) => byId.get(job.id) || job));
		const extra = [pinUpdate, ...followedUpdates].filter((j) => j && !byId.has(j.id)).map(withLocal);
		const seen = [...byId.values(), ...extra];
		track(seen);
		report(seen);
		if (finished) await load();
	}, [tagId, view, load]);

	const polling = jobs.some(isActiveJob) || isActiveJob(pinned) || Object.values(tracked).some(isActiveJob);
	useVisibleInterval(poll, pollDelay(pollFailures), polling);

	const setView = useCallback((next) => {
		setViewState(next);
		setPage(1);
	}, []);

	// A local change wins over any read already in flight: make them stale,
	// then read again.
	const settleLocal = useCallback(() => {
		latestRequest.current += 1;
		loadInFlight.current = false;
		load();
	}, [load]);

	// A job this page just submitted: on top of the first page (where the API
	// lists it), then a fresh read. Off the first page, go back to it.
	const addSubmitted = useCallback(
		(job) => {
			if (!job) return;
			if (isActiveJob(job)) setTracked((prev) => ({ ...prev, [job.id]: job }));
			if (page !== 1) {
				latestRequest.current += 1;
				setPage(1);
				return;
			}
			setJobs((prev) => [job, ...prev.filter((j) => j.id !== job.id)]);
			settleLocal();
		},
		[page, settleLocal],
	);

	// A job this page changed (a cancel): replace it where it is shown, the
	// pinned row or the list, never add it to a page or filter it is not on.
	const replaceJob = useCallback(
		(job) => {
			if (!job) return;
			setPinnedState((p) => (p && p.id === job.id ? job : p));
			setJobs((prev) => prev.map((j) => (j.id === job.id ? job : j)));
			setTracked((prev) => (prev[job.id] ? { ...prev, [job.id]: job } : prev));
			settleLocal();
		},
		[settleLocal],
	);

	// The download answered 410: the link has lapsed, whatever the row said.
	const markExpired = useCallback(
		(jobId) => {
			expiredIds.current.add(jobId);
			const expire = (job) => (job.id === jobId ? { ...job, status: "expired", download_available: false } : job);
			setJobs((prev) => prev.map(expire));
			setPinnedState((p) => (p ? expire(p) : p));
			settleLocal();
		},
		[settleLocal],
	);

	const setPinned = useCallback((job) => setPinnedState(job ? withLocal(job) : null), []);

	const rows = pinned && !jobs.some((j) => j.id === pinned.id) ? [pinned, ...jobs] : jobs;

	return {
		view,
		setView,
		page,
		setPage,
		rows,
		pagination,
		loading,
		loaded,
		loadError,
		pollError,
		reload: load,
		addSubmitted,
		replaceJob,
		markExpired,
		setPinned,
		pinnedId: pinned ? pinned.id : null,
	};
}
